<!--
  瀑布流图片组件
  用途：按最短列算法排布商品图片，形成瀑布流展示
  用法：
    <WaterImageFall
      :images="productList"
      :column-width="280"
      :gutter="16"
      @item-click="onProductClick"
    />
  说明：images 项需包含 src；点击图片会向父组件抛出 item-click 事件
-->
<template>
  <div ref="container" class="waterfall-container">
    <div
      v-for="(item, index) in items"
      :key="item.id || index"
      class="waterfall-item"
      :style="itemStyle(item)"
      @click="emit('item-click', item)"
    >
      <img :src="item.src" class="waterfall-img" :alt="item.name || '商品图片'" />
      <div v-if="item.name" class="waterfall-meta">
        <span class="waterfall-name">{{ item.name }}</span>
        <span class="waterfall-price">{{ item.price }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  /** 商品图片列表，项结构：{ id, src, name?, price?, ... } */
  images: {
    type: Array,
    required: true
  },
  /** 每列基础宽度（像素） */
  columnWidth: {
    type: Number,
    default: 280
  },
  /** 图片间距（像素） */
  gutter: {
    type: Number,
    default: 16
  }
})

const emit = defineEmits(['item-click'])

const container = ref(null)
const items = ref([])
let resizeObserver = null

/** 预加载图片以获取真实宽高，保证瀑布流高度计算准确 */
const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.src = url
    img.onload = () => resolve(img)
    img.onerror = reject
  })
}

/**
 * 计算瀑布流布局
 * 算法：始终将下一张图放入当前最短列，实现近似等高的多列排布
 */
const computeLayout = async () => {
  if (!container.value) return

  let imagesData = []
  try {
    imagesData = await Promise.all(
      props.images.map(async (image) => {
        const img = await loadImage(image.src)
        return {
          ...image,
          width: img.naturalWidth,
          height: img.naturalHeight
        }
      })
    )
  } catch (error) {
    // 个别图片失败时降级使用原始列表，避免整页空白
    imagesData = props.images.map((image) => ({
      ...image,
      width: props.columnWidth,
      height: props.columnWidth * 1.2
    }))
  }

  const containerWidth = container.value.offsetWidth
  const numColumns = Math.max(
    1,
    Math.floor((containerWidth + props.gutter) / (props.columnWidth + props.gutter))
  )
  // 根据列数自适应列宽，保证左右对齐容器
  const displayWidth =
    (containerWidth - props.gutter * (numColumns - 1)) / numColumns
  const columnHeights = new Array(numColumns).fill(0)
  const newItems = []

  for (const imgData of imagesData) {
    const ratio = imgData.height / imgData.width || 1.2
    const imageHeight = displayWidth * ratio
    // 有文案时预留底部信息条高度
    const metaHeight = imgData.name ? 64 : 0
    const displayHeight = imageHeight + metaHeight

    const minHeight = Math.min(...columnHeights)
    const columnIndex = columnHeights.indexOf(minHeight)
    const left = columnIndex * (displayWidth + props.gutter)
    const top = columnHeights[columnIndex]

    newItems.push({
      ...imgData,
      left,
      top,
      width: displayWidth,
      height: displayHeight,
      imageHeight
    })

    columnHeights[columnIndex] += displayHeight + props.gutter
  }

  const maxCol = columnHeights.length ? Math.max(...columnHeights) : 0
  const totalHeight = Math.max(0, maxCol > 0 ? maxCol - props.gutter : 0)
  container.value.style.height = `${totalHeight}px`
  items.value = newItems
}

const itemStyle = (item) => ({
  left: `${item.left}px`,
  top: `${item.top}px`,
  width: `${item.width}px`,
  height: `${item.height}px`
})

onMounted(() => {
  resizeObserver = new ResizeObserver(() => computeLayout())
  if (container.value) {
    resizeObserver.observe(container.value)
  }
  computeLayout()
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})

watch(() => props.images, computeLayout, { deep: true })
watch(() => props.columnWidth, computeLayout)
watch(() => props.gutter, computeLayout)
</script>

<style scoped>
.waterfall-container {
  position: relative;
  z-index: 1;
  width: 100%;
  margin: 0 auto;
  /* 裁切绝对定位卡片，避免盖住下方页脚 */
  overflow: hidden;
  transition: height 0.3s ease;
  isolation: isolate;
}

.waterfall-item {
  position: absolute;
  overflow: hidden;
  border-radius: var(--radius);
  background: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.waterfall-item:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-soft);
}

.waterfall-img {
  width: 100%;
  height: calc(100% - 64px);
  object-fit: cover;
  display: block;
}

.waterfall-meta {
  height: 64px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  text-align: left;
}

.waterfall-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.waterfall-price {
  font-size: 13px;
  color: var(--color-primary);
  font-weight: 600;
}
</style>
